import { CustomerGroups } from '@adapters/bigcommerce/types/customer';
import { PriceGroup } from '@adapters/bigcommerce/types/product';

import type { MultiGroupPrices } from '@adapters/bigcommerce/operations/getAllCustomerGroupPrices';

type MultiGroupPriceResponse = MultiGroupPrices;
export type PriceGroupKey = keyof Omit<MultiGroupPrices, 'hasError'>;

export interface CustomerPriceGroupInfo {
  groupKey: PriceGroupKey;
  customerGroupId: number | undefined;
}

export interface PriceSelectionResult {
  price: PriceGroup | null;
  groupKey: PriceGroupKey;
  hasFallback: boolean;
  source:
    | 'exact'
    | 'guest-fallback'
    | 'aggregated-fallback'
    | 'unknown-group-fallback'
    | 'not-found';
}

/**
 * Maps customer group IDs to price list keys.
 * Customer group IDs are sourced from `NEXT_PUBLIC_BIGCOMMERCE_*_GROUP_ID` env vars.
 */
export const getGroupKeyByCustomerGroupId = (
  customerGroupId: number
): PriceGroupKey => {
  // Map customer group ID to price list key
  switch (customerGroupId) {
    case CustomerGroups.Member:
      return 'member';
    case CustomerGroups.Employee:
      return 'employee';
    case CustomerGroups.Family:
    case CustomerGroups.Honorary:
    case CustomerGroups.Ambassadors:
    case CustomerGroups.Employee20Year:
      return 'family';
    case CustomerGroups.ACMG:
      return 'acmg';
    case CustomerGroups.CAFFR:
      return 'caffr';
    case CustomerGroups.Guest:
    default:
      return 'guest';
  }
};

/**
 * Checks if a customer group ID is a known/valid customer group
 * @param customerGroupId - Customer group ID to check
 * @returns true if the group ID is recognized, false otherwise
 */
export const isKnownCustomerGroupId = (customerGroupId: number): boolean => {
  return Object.values(CustomerGroups).includes(customerGroupId);
};

/**
 * Selects the appropriate price from multi-group price response with metadata about actual selection
 * Returns information about which price list was actually used, including fallback information
 *
 * @param prices - Multi-group price response from API
 * @param customerGroupId - Customer's group ID from customer context
 * @param variantId - Optional variant ID to get variant-specific price
 * @returns PriceSelectionResult with price and metadata about the selection
 *
 * Source values:
 * - 'exact': Found the exact price requested (variant-specific or product-level)
 * - 'aggregated-fallback': Requested variant not found, fell back to product-level price
 * - 'guest-fallback': Requested customer group not found, fell back to guest prices
 * - 'unknown-group-fallback': Unknown customer group ID provided, fell back to guest prices
 * - 'not-found': No price data available
 *
 * @example
 * ```tsx
 * const result = selectPriceByGroupWithMetadata(
 *   customerGroupPrices,
 *   customerGroupId,
 *   variantId
 * );
 *
 * // result.price - The selected price
 * // result.groupKey - Actual group used ('member', 'guest', etc.)
 * // result.hasFallback - true if any fallback occurred
 * // result.source - See source values above
 * ```
 */
export const selectPriceByGroupWithMetadata = (
  prices: MultiGroupPriceResponse | null,
  customerGroupId: number | undefined,
  variantId?: number
): PriceSelectionResult => {
  if (!prices) {
    return {
      price: null,
      groupKey: 'guest',
      hasFallback: false,
      source: 'not-found',
    };
  }

  // Handle unknown customer group IDs
  if (
    customerGroupId !== undefined &&
    !isKnownCustomerGroupId(customerGroupId)
  ) {
    console.warn(
      `Unknown customer group ID: ${customerGroupId}. Falling back to guest pricing.`
    );

    const guestPrice = prices.guest?.price ?? null;
    return {
      price: guestPrice,
      groupKey: 'guest',
      hasFallback: true,
      source: 'unknown-group-fallback',
    };
  }

  // Default to guest prices if no customer group
  const requestedGroupKey = customerGroupId
    ? getGroupKeyByCustomerGroupId(customerGroupId)
    : 'guest';

  const groupData = prices[requestedGroupKey];

  if (!groupData) {
    console.error(
      `Price data not found for group: ${requestedGroupKey}. Falling back to guest prices.`
    );

    const guestPrice = prices.guest?.price ?? null;
    return {
      price: guestPrice,
      groupKey: 'guest',
      hasFallback: requestedGroupKey !== 'guest',
      source: guestPrice ? 'guest-fallback' : 'not-found',
    };
  }

  // If variant ID is provided, find variant-specific price
  if (variantId !== undefined) {
    const variant = groupData.variants.find((v) => v.entityId === variantId);

    if (variant) {
      return {
        price: variant.price,
        groupKey: requestedGroupKey,
        hasFallback: false,
        source: 'exact',
      };
    }

    // Fallback to aggregated price if variant not found
    console.warn(
      `Variant ${variantId} not found in ${requestedGroupKey} variant price data. Falling back to aggregated price.`
    );

    return {
      price: groupData.price,
      groupKey: requestedGroupKey,
      hasFallback: true,
      source: 'aggregated-fallback',
    };
  }

  // Return aggregated product-level price (no variant requested, this is the expected price)
  return {
    price: groupData.price,
    groupKey: requestedGroupKey,
    hasFallback: false,
    source: 'exact',
  };
};

/**
 * Gets all available price groups from the multi-group response
 * Useful for displaying price comparisons or debugging
 *
 * @param prices - Multi-group price response from API
 * @returns Object with all price groups
 */
export const getAllPriceGroups = (
  prices: MultiGroupPriceResponse | null
): Record<PriceGroupKey, PriceGroup | null> => {
  if (!prices) {
    return {
      guest: null,
      member: null,
      employee: null,
      family: null,
      acmg: null,
      caffr: null,
    };
  }

  return {
    guest: prices.guest?.price ?? null,
    member: prices.member?.price ?? null,
    employee: prices.employee?.price ?? null,
    family: prices.family?.price ?? null,
    acmg: prices.acmg?.price ?? null,
    caffr: prices.caffr?.price ?? null,
  };
};

/**
 * Gets information about the customer's price group based on customer group ID
 * Useful for displaying price group indicators and differentiating member/guest pricing
 *
 * @param customerGroupId - Customer's group ID from customer context
 * @returns CustomerPriceGroupInfo with group key and customer group ID
 */
export const getPriceListInfo = (
  customerGroupId: number | undefined
): CustomerPriceGroupInfo => {
  const groupKey = customerGroupId
    ? getGroupKeyByCustomerGroupId(customerGroupId)
    : 'guest';

  return {
    groupKey,
    customerGroupId,
  };
};

export default selectPriceByGroupWithMetadata;
